import { useEffect, useMemo, useRef, useState } from "react"
import { OK, ERROR, type DataStatus } from "helpers/data-statuses"
import { isUuid } from "helpers/types/uuid"
import { useAccountAlias, useAreEnabledFeaturesResolved, useHaveEnabledFeaturesFailed, useIsMultiDashboardsEnabled } from "resources/account"
import { useDashboardInstances, type DashboardInstance } from "resources/dashboard-instances"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { useAppDispatch } from "hooks/redux"
import { DEFAULT_DASHBOARD_ID } from "../context/dashboard-id"
import { instancesSlice, pickMostRecentInstanceId } from "../store/instances-slice"

// Discriminated result the layout acts on: render the resolved dashboard, wait,
// replace-navigate the unknown/foreign/deleted id back to bare /dashboard, or replace-navigate a
// non-canonical route (e.g. a raw instanceId) to the instance's slug.
export type RouteResolution =
  | { kind: "loading" }
  | { kind: "resolved", dashboardId: string }
  | { kind: "redirect" }
  | { kind: "canonicalize", dashboardId: string, slug: string }

const EMPTY_INSTANCES: DashboardInstance[] = []
const LOADING: RouteResolution = { kind: "loading" }

// Resolves the dashboardId to render for /dashboard[/:id]. Flag-off short-circuits to
// today's single-dashboard path (no fetch/fallback/toast).
export function useDashboardRouteResolution(routeDashboardId: string | undefined): RouteResolution {
  const isEnabled = useIsMultiDashboardsEnabled()
  const accountAlias = useAccountAlias()
  const areFeaturesResolved = useAreEnabledFeaturesResolved()
  const haveFeaturesFailed = useHaveEnabledFeaturesFailed()
  const [instances, status]: [DashboardInstance[] | null, DataStatus] = useDashboardInstances(isEnabled)
  const dispatch = useAppDispatch()

  // Fires the "not found" toast at most once per offending id. Keyed by id (not a
  // boolean) so a later distinct bad id still notifies.
  const toastedIdRef = useRef<string | null>(null)

  const instanceList = instances ?? EMPTY_INSTANCES
  // From the fetched list, NOT the registry slice: the slice is hydrated by the effect below (after
  // paint), so on the first settled render it's still empty and would briefly resolve "default".
  const mostRecentId = useMemo(() => pickMostRecentInstanceId(instanceList), [instanceList])

  useEffect(() => {
    // No length guard: an empty list must still hydrate so a genuine emptying clears
    // the registry. setInstances no-op-skips an empty-vs-empty write itself.
    if (!isEnabled || status !== OK) return
    dispatch(instancesSlice.actions.setInstances(instanceList))
  }, [isEnabled, status, instanceList, dispatch])

  // A resource gate must accept ERROR too: gating on OK alone hangs on <Loading/>
  // forever when the fetch errors. On ERROR we fall through to the client default.
  const isSettled = status === OK || status === ERROR

  const hasRouteId = routeDashboardId != null && routeDashboardId !== ""
  // The URL segment may be a slug (preferred) or raw instanceId; match either, then resolve to the
  // canonical instanceId so everything downstream stays keyed by uuid.
  const matchedInstance = useMemo(
    () => (hasRouteId
      ? instanceList.find((instance) => instance.slug === routeDashboardId || instance.instanceId === routeDashboardId)
      : undefined),
    [hasRouteId, routeDashboardId, instanceList],
  )

  // An unmatched id is "unknown" (→ redirect + toast) only when UUID-shaped (a deleted/foreign
  // instance). An unmatched non-UUID is a legacy slug dashboard kept in preferences — resolve it
  // as-is so the lazy-migration gate can import it. Flag-off never validates, so never toasts/redirects.
  const isUnmigratedSlug = hasRouteId && !matchedInstance && !isUuid(routeDashboardId)
  const isUnknownRouteId = isEnabled && isSettled && hasRouteId && !matchedInstance && !isUnmigratedSlug

  useEffect(() => {
    if (!isUnknownRouteId || routeDashboardId == null) return
    if (toastedIdRef.current === routeDashboardId) return
    toastedIdRef.current = routeDashboardId
    // Identical message for foreign vs nonexistent — no info leak.
    dispatch(NotificationCreators.add("error", null, "Dashboard not found."))
  }, [isUnknownRouteId, routeDashboardId, dispatch])

  const resolution: RouteResolution = areFeaturesResolved || haveFeaturesFailed
    ? resolveRoute({ isEnabled, isSettled, routeDashboardId, matchedInstance, isUnmigratedSlug, mostRecentId })
    : LOADING

  const [held, setHeld] = useState<HeldResolution | null>(null)
  const routeKey = `${accountAlias}/${routeDashboardId ?? ""}`
  if (resolution.kind === "resolved" && (held?.routeKey !== routeKey || held.dashboardId !== resolution.dashboardId)) {
    setHeld({ routeKey, dashboardId: resolution.dashboardId })
  }

  if (resolution.kind === "loading" && held?.routeKey === routeKey) {
    return { kind: "resolved", dashboardId: held.dashboardId }
  }
  return resolution
}

interface HeldResolution {
  routeKey: string
  dashboardId: string
}

interface RouteResolutionInputs {
  isEnabled: boolean
  isSettled: boolean
  routeDashboardId: string | undefined
  matchedInstance: DashboardInstance | undefined
  isUnmigratedSlug: boolean
  mostRecentId: string
}

function resolveRoute({
  isEnabled,
  isSettled,
  routeDashboardId,
  matchedInstance,
  isUnmigratedSlug,
  mostRecentId,
}: RouteResolutionInputs): RouteResolution {
  const hasRouteId = routeDashboardId != null && routeDashboardId !== ""
  if (!isEnabled) {
    // Today's behavior: empty path falls back, "" must not become a distinct dashboard.
    return { kind: "resolved", dashboardId: routeDashboardId || DEFAULT_DASHBOARD_ID }
  }

  if (!isSettled) {
    return { kind: "loading" }
  }

  // The `!= null` re-check is redundant with hasRouteId but narrows routeDashboardId to string.
  if (hasRouteId && routeDashboardId != null) {
    if (matchedInstance) {
      // Route addressed by a non-canonical id (e.g. create navigates by instanceId to dodge the
      // lazy-migration race) — now that it's confirmed to match, correct the URL to the slug
      // instead of leaving it on a raw UUID indefinitely.
      if (routeDashboardId !== matchedInstance.slug) {
        return { kind: "canonicalize", dashboardId: matchedInstance.instanceId, slug: matchedInstance.slug }
      }
      return { kind: "resolved", dashboardId: matchedInstance.instanceId }
    }
    // Unmatched UUID → deleted/foreign instance → redirect. Unmatched slug → unmigrated legacy
    // dashboard → resolve to the slug so the migration gate imports it (name inferred from slug).
    if (isUnmigratedSlug) return { kind: "resolved", dashboardId: routeDashboardId }
    return { kind: "redirect" }
  }

  // Bare /dashboard: most-recent instance, else DEFAULT_DASHBOARD_ID (starter-template server POST
  // is deferred — client createDefaultModules renders).
  return { kind: "resolved", dashboardId: mostRecentId }
}
